import { DropdownMenuItem } from "@/components/atoms/DropdownMenuItem/DropdownMenuItem";
import { IconButton, type IconSize } from "@/components/atoms/IconButton/IconButton";
import { DropdownMenu } from "@/components/molecules/DropdownMenu/DropdownMenu";
import { NestedDropdownMenuItem } from "@/components/molecules/DropdownMenu/NestedDropdownMenuItem/NestedDropdownMenuItem";
import type { MenuItem } from "@/components/types";
import type React from "react";
import { Fragment, useRef, useState } from "react";
import "./OptionsMenu.scss";

export interface OptionsMenuItem extends MenuItem {
  nestedComponent?: React.ReactNode;
}

interface OptionsMenuProps {
  items: OptionsMenuItem[];
  iconName?: string;
  iconSize?: IconSize;
  ariaLabel?: string;
  className?: string;
  testId?: string;
}

export const OptionsMenu: React.FC<OptionsMenuProps> = ({
  items,
  iconName = "more_vert",
  iconSize = "medium",
  ariaLabel = "Open options menu",
  className = "more-button",
  testId,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const closeMenu = () => setIsOpen(false);
  const toggleMenu = () => setIsOpen(!isOpen);

  return (
    <div className="options-menu-wrapper">
      <IconButton
        ref={buttonRef}
        iconName={iconName}
        size={iconSize}
        ariaLabel={ariaLabel}
        className={className}
        data-testid={testId}
        onClick={toggleMenu}
      />
      <DropdownMenu
        isOpen={isOpen}
        withinPortal={true}
        placement={"bottom-end"}
        anchorRef={buttonRef}
        onClose={closeMenu}
      >
        {items.map((item) => (
          <Fragment key={item.id}>
            {item.nestedComponent ? (
              <NestedDropdownMenuItem
                label={item.label}
                iconName={item.iconName}
                hoverComponent={item.nestedComponent}
              />
            ) : (
              <DropdownMenuItem
                label={item.label}
                onClick={() => {
                  item.onSelect();
                  closeMenu();
                }}
                iconName={item.iconName}
                isDestructive={item.isDestructive}
                disabled={item.disabled}
                testId={item.id ? `options-item-${item.id}` : undefined}
              />
            )}
          </Fragment>
        ))}
      </DropdownMenu>
    </div>
  );
};
